import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { Toaster, toast } from "sonner";
import ExperienceAdmin from "@/components/admin/ExperienceAdmin";
import AboutAdmin from "@/components/admin/AboutAdmin";
import HeroAdmin from "@/components/admin/HeroAdmin";
import ServicesAdmin from "@/components/admin/ServicesAdmin";
import TestimonialsAdmin from "@/components/admin/TestimonialsAdmin";
import FooterAdmin from "@/components/admin/FooterAdmin";
import MarqueeAdmin from "@/components/admin/MarqueeAdmin";
import SEO from "@/components/SEO";

const EMPTY_FORM = {
  title: "", category: "", year: new Date().getFullYear().toString(),
  description: "", image_url: "", tags: "", client: "", role: "", link: "", featured: false,
};

export default function Admin() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(location.state?.user || null);
  const [authed, setAuthed] = useState(location.state?.user ? true : null);
  const [tab, setTab] = useState("projects");
  const [projects, setProjects] = useState([]);
  const [messages, setMessages] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image is larger than 10MB.");
      return;
    }
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const r = await api.post("/uploads", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const absoluteUrl = `${BACKEND}${r.data.url}`;
      setForm((f) => ({ ...f, image_url: absoluteUrl }));
      toast.success("Image uploaded.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  useEffect(() => {
    if (location.state?.user) return;
    api.get("/auth/me")
      .then((r) => {
        if (!r.data.is_admin) throw new Error("not admin");
        setUser(r.data);
        setAuthed(true);
      })
      .catch(() => {
        setAuthed(false);
        navigate("/admin/login", { replace: true });
      });
  }, [navigate, location.state]);

  const loadProjects = () => api.get("/projects").then((r) => setProjects(r.data));
  const loadMessages = () => api.get("/contact").then((r) => setMessages(r.data));

  useEffect(() => {
    if (authed) {
      loadProjects();
      loadMessages();
    }
  }, [authed]);

  const onChange = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const startEdit = (p) => {
    setEditingId(p.id);
    setForm({
      title: p.title || "",
      category: p.category || "",
      year: p.year || "",
      description: p.description || "",
      image_url: p.image_url || "",
      tags: (p.tags || []).join(", "),
      client: p.client || "",
      role: p.role || "",
      link: p.link || "",
      featured: !!p.featured,
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const submitProject = async (e) => {
    e.preventDefault();
    if (!form.title || !form.category || !form.description || !form.image_url) {
      toast.error("Title, category, description and image URL are required.");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (editingId) {
        await api.put(`/projects/${editingId}`, payload);
        toast.success("Project updated.");
      } else {
        await api.post("/projects", payload);
        toast.success("Project created.");
      }
      cancelEdit();
      await loadProjects();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save project.");
    } finally {
      setSaving(false);
    }
  };

  const deleteProject = async (id) => {
    if (!window.confirm("Delete this project?")) return;
    try {
      await api.delete(`/projects/${id}`);
      toast.success("Project deleted.");
      if (editingId === id) cancelEdit();
      await loadProjects();
    } catch {
      toast.error("Failed to delete.");
    }
  };

  const deleteMessage = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    try {
      await api.delete(`/contact/${id}`);
      toast.success("Message deleted.");
      await loadMessages();
    } catch {
      toast.error("Failed to delete.");
    }
  };

  const logout = async () => {
    await api.post("/auth/logout");
    navigate("/admin/login", { replace: true });
  };

  if (authed === null) {
    return (
      <div className="min-h-screen bg-[#F4F4F0] flex items-center justify-center font-body text-sm text-neutral-500" data-testid="admin-loading">
        Loading…
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F0] text-[#0A0A0A]" data-testid="admin-page">
      <SEO title="Studio Control — Admin" noindex />
      <Toaster position="bottom-right" />

      <header className="border-b border-neutral-300 px-6 md:px-12 py-6 flex items-center justify-between sticky top-0 bg-[#F4F4F0]/90 backdrop-blur-xl z-10">
        <div className="flex items-center gap-6">
          <a href="/" className="font-display font-black text-lg tracking-tight" data-testid="admin-logo-link">
            STUDIO/SAAR<span className="text-[#FF3E00]">®</span>
          </a>
          <span className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">Admin</span>
        </div>
        <div className="flex items-center gap-6">
          {user && (
            <span className="font-body text-xs text-neutral-700 hidden md:block" data-testid="admin-user-email">
              {user.email}
            </span>
          )}
          <button onClick={logout} className="font-body text-xs uppercase tracking-[0.25em] link-line" data-testid="admin-logout-button">
            Sign out
          </button>
        </div>
      </header>

      <div className="px-6 md:px-12 py-10">
        <div className="flex items-end justify-between mb-10 flex-wrap gap-4">
          <h1 className="font-display font-black uppercase tracking-tighter text-5xl sm:text-7xl leading-[0.9]" data-testid="admin-heading">
            Studio<br /><span className="font-serif-italic font-light normal-case lowercase text-[#FF3E00]">control</span>
          </h1>
          <div className="flex flex-wrap gap-2 border border-neutral-300">
            {[
              { id: "projects", label: `Projects (${projects.length})` },
              { id: "messages", label: `Messages (${messages.length})` },
              { id: "experience", label: "Experience" },
              { id: "me", label: "Me" },
              { id: "hero", label: "Hero" },
              { id: "services", label: "Services" },
              { id: "testimonials", label: "Testimonials" },
              { id: "marquees", label: "Marquees" },
              { id: "footer", label: "Footer" },
            ].map((t) => (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                className={`px-5 py-3 font-body text-xs uppercase tracking-[0.25em] transition-colors ${tab === t.id ? "bg-[#0A0A0A] text-[#F4F4F0]" : ""}`}
                data-testid={`tab-${t.id}-button`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        {tab === "projects" && (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <section className="lg:col-span-7" data-testid="projects-list-section">
              <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-6">All Projects</p>
              <div className="space-y-3">
                {projects.map((p) => (
                  <div
                    key={p.id}
                    className={`bg-white border p-4 flex gap-4 items-center ${editingId === p.id ? "border-[#FF3E00]" : "border-neutral-200"}`}
                    data-testid={`admin-project-row-${p.id}`}
                  >
                    <img src={p.image_url} alt={p.title} className="w-20 h-20 object-cover flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <div className="font-display font-bold text-lg truncate">{p.title}</div>
                      <div className="font-body text-xs text-neutral-500">
                        /work/{p.slug} · {p.category} · {p.year} {p.featured && " · Featured"}
                      </div>
                    </div>
                    <a
                      href={`/work/${p.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-body text-xs uppercase tracking-[0.2em] link-line"
                      data-testid={`view-project-${p.id}`}
                    >
                      View
                    </a>
                    <button
                      onClick={() => startEdit(p)}
                      className="font-body text-xs uppercase tracking-[0.2em] link-line"
                      data-testid={`edit-project-${p.id}`}
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => deleteProject(p.id)}
                      className="font-body text-xs uppercase tracking-[0.2em] text-[#FF3E00] link-line"
                      data-testid={`delete-project-${p.id}`}
                    >
                      Delete
                    </button>
                  </div>
                ))}
                {projects.length === 0 && <p className="font-body text-sm text-neutral-500">No projects yet.</p>}
              </div>
            </section>

            <aside className="lg:col-span-5">
              <div className="flex items-center justify-between mb-6">
                <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500">
                  {editingId ? "Edit Project" : "New Project"}
                </p>
                {editingId && (
                  <button onClick={cancelEdit} className="font-body text-xs uppercase tracking-[0.2em] link-line" data-testid="cancel-edit-button">
                    Cancel
                  </button>
                )}
              </div>
              <form onSubmit={submitProject} className="space-y-1 bg-white border border-neutral-200 p-6" data-testid="project-form">
                <input value={form.title} onChange={onChange("title")} placeholder="Title" className="input-bare" data-testid="project-title-input" />
                <input value={form.category} onChange={onChange("category")} placeholder="Category" className="input-bare" data-testid="project-category-input" />
                <input value={form.year} onChange={onChange("year")} placeholder="Year" className="input-bare" data-testid="project-year-input" />
                <input value={form.image_url} onChange={onChange("image_url")} placeholder="Image URL" className="input-bare" data-testid="project-image-input" />
                <div className="pt-3 flex items-center gap-3 flex-wrap">
                  <label
                    className="inline-flex items-center gap-2 px-4 py-2 border border-neutral-300 font-body text-xs uppercase tracking-[0.2em] cursor-pointer hover:bg-neutral-50"
                    data-testid="upload-image-label"
                  >
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
                      onChange={handleFileUpload}
                      className="hidden"
                      disabled={uploading}
                      data-testid="upload-image-input"
                    />
                    {uploading ? "Uploading…" : "↑ Upload image"}
                  </label>
                  {form.image_url && (
                    <img
                      src={form.image_url}
                      alt="preview"
                      className="w-14 h-14 object-cover border border-neutral-200"
                      data-testid="upload-image-preview"
                    />
                  )}
                </div>
                <input value={form.client} onChange={onChange("client")} placeholder="Client" className="input-bare" data-testid="project-client-input" />
                <input value={form.role} onChange={onChange("role")} placeholder="Role" className="input-bare" data-testid="project-role-input" />
                <input value={form.link} onChange={onChange("link")} placeholder="External link (optional)" className="input-bare" data-testid="project-link-input" />
                <input value={form.tags} onChange={onChange("tags")} placeholder="Tags (comma separated)" className="input-bare" data-testid="project-tags-input" />
                <textarea value={form.description} onChange={onChange("description")} placeholder="Description" rows={3} className="input-bare resize-none" data-testid="project-description-input" />
                <label className="flex items-center gap-2 pt-4 font-body text-sm">
                  <input type="checkbox" checked={form.featured} onChange={onChange("featured")} data-testid="project-featured-checkbox" />
                  Featured
                </label>
                <button
                  type="submit"
                  disabled={saving}
                  className="mt-6 w-full bg-[#0A0A0A] text-[#F4F4F0] py-4 font-display font-bold uppercase tracking-widest text-sm hover:bg-[#FF3E00] transition-colors disabled:opacity-40"
                  data-testid="save-project-button"
                >
                  {saving ? "Saving…" : editingId ? "Update Project" : "Create Project"}
                </button>
              </form>
            </aside>
          </div>
        )}

        {tab === "messages" && (
          <section data-testid="messages-section">
            <p className="font-body text-xs uppercase tracking-[0.25em] text-neutral-500 mb-6">Contact Messages</p>
            <div className="space-y-3">
              {messages.map((m) => (
                <article key={m.id} className="bg-white border border-neutral-200 p-6" data-testid={`message-row-${m.id}`}>
                  <div className="flex justify-between items-start gap-4 mb-3">
                    <div>
                      <div className="font-display font-bold text-xl">{m.name}</div>
                      <a href={`mailto:${m.email}`} className="font-body text-sm text-neutral-600 link-line">{m.email}</a>
                    </div>
                    <div className="text-right">
                      <div className="font-body text-xs uppercase tracking-[0.2em] text-neutral-500">
                        {new Date(m.created_at).toLocaleString()}
                      </div>
                      <button onClick={() => deleteMessage(m.id)} className="font-body text-xs uppercase tracking-[0.2em] text-[#FF3E00] link-line mt-2" data-testid={`delete-message-${m.id}`}>
                        Delete
                      </button>
                    </div>
                  </div>
                  {m.subject && <div className="font-display font-semibold text-base mb-2">{m.subject}</div>}
                  <p className="font-body text-sm text-neutral-700 whitespace-pre-wrap">{m.message}</p>
                </article>
              ))}
              {messages.length === 0 && <p className="font-body text-sm text-neutral-500">No messages yet.</p>}
            </div>
          </section>
        )}

        {tab === "experience" && <ExperienceAdmin />}
        {tab === "me" && <AboutAdmin />}
        {tab === "hero" && <HeroAdmin />}
        {tab === "services" && <ServicesAdmin />}
        {tab === "testimonials" && <TestimonialsAdmin />}
        {tab === "marquees" && <MarqueeAdmin />}
        {tab === "footer" && <FooterAdmin />}
      </div>
    </div>
  );
}
